import { logger } from '@inboundx/shared/platform';

import { supabase } from '@/lib/supabase';
import { fetchAdministeredDomains } from '@/services/teamAdmin';

export interface BackofficeUser {
    id: string;
    user_id: string | null;
    email: string;
    is_admin: boolean;
    is_active: boolean;
    display_name: string | null;
}

export interface BackofficeUserWithDomains extends BackofficeUser {
    accessible_domains: string[];
    /** Whether the user is a client account admin of at least one account (IX-3872). */
    is_account_admin: boolean;
    /** Domains of the accounts the user is an account admin of (IX-4441). */
    admin_domains: string[];
}

/**
 * Fetch the current user's backoffice profile using RPC function
 * Returns null if user is not in backoffice_users table (no access)
 */
async function getBackofficeUserProfile(): Promise<BackofficeUser | null> {
    const { data, error } = await supabase.rpc('get_backoffice_user_profile');

    if (error) {
        // Throw, never return null: a swallowed RPC error is indistinguishable
        // from "no backoffice row", so it dumped fully-provisioned users into the
        // onboarding wizard. Throwing lets React Query retry/refetch and lets the
        // caller tell a failed fetch apart from a genuine new user.
        logger.error('Error fetching backoffice user profile:', error);
        throw new Error(error.message);
    }

    if (!data || data.length === 0) {
        return null;
    }

    // RPC returns an array, take the first (and only) row
    const row = data[0];
    return {
        id: row.id,
        user_id: row.user_id,
        email: row.email,
        is_admin: row.is_admin,
        is_active: row.is_active,
        display_name: row.display_name,
    };
}

/**
 * Fetch accessible domains for the current user
 */
async function getAccessibleDomains(): Promise<string[]> {
    const { data, error } = await supabase.rpc('get_accessible_domains');

    if (error) {
        logger.error('Error fetching accessible domains:', error);
        return [];
    }

    return data || [];
}

/**
 * Fetch the current user's backoffice profile with accessible domains
 * Returns null if user is not in backoffice_users table (no access)
 */
export async function getBackofficeUser(): Promise<BackofficeUserWithDomains | null> {
    const profile = await getBackofficeUserProfile();

    if (!profile) {
        return null;
    }

    // adminDomains doubles as the account-admin flag: administering an account
    // means administering its domains, so a non-empty list is the flag.
    const [domains, adminDomains] = await Promise.all([
        getAccessibleDomains(),
        fetchAdministeredDomains(),
    ]);

    return {
        ...profile,
        accessible_domains: domains,
        is_account_admin: adminDomains.length > 0,
        admin_domains: adminDomains,
    };
}

/**
 * Try to link current auth user to their backoffice_users record by email.
 * This is called on login to handle pre-provisioned users.
 * Returns true if linking was successful or user was already linked.
 */
export async function tryLinkBackofficeUser(): Promise<boolean> {
    const {
        data: { user },
    } = await supabase.auth.getUser();

    if (!user?.email) {
        return false;
    }

    // Call RPC function to link user (not yet in generated types)
    const { error } = await supabase.rpc('link_current_user_to_backoffice' as 'is_backoffice_user');

    if (error) {
        // Not an error if user doesn't exist in backoffice_users - they just don't have access
        logger.debug('Could not link backoffice user:', error.message);
        return false;
    }

    return true;
}
